/**
 * Structured Logging Service
 * 
 * Provides consistent logging across the application with proper log levels,
 * context, and metadata. Integrates with external logging services in production.
 */

export enum LogLevel {
  DEBUG = 'debug',
  INFO = 'info',
  WARN = 'warn',
  ERROR = 'error',
}

export interface LogMetadata {
  [key: string]: unknown;
}

export interface LogEntry {
  level: LogLevel;
  message: string;
  timestamp: string;
  metadata?: LogMetadata;
}

class Logger {
  /**
   * Safely checks if running in development mode
   * Uses runtime checks instead of importing from config to avoid
   * initialization errors when API key is missing
   */
  private isDevelopment(): boolean {
    // Check Vite environment
    if (typeof import.meta !== 'undefined' && import.meta.env) {
      return import.meta.env.DEV;
    }
    // Fallback to Node environment
    return process.env.NODE_ENV !== 'production';
  }

  /**
   * Formats a log entry for output
   */
  private formatLogEntry(entry: LogEntry): string {
    const { level, message, timestamp, metadata } = entry;
    
    if (this.isDevelopment()) {
      // Human-readable console output for development
      const metaStr = metadata ? `\n${JSON.stringify(metadata, null, 2)}` : '';
      return `[${timestamp}] ${level.toUpperCase()}: ${message}${metaStr}`;
    }
    
    // Structured JSON for production (easier to parse by log aggregators)
    return JSON.stringify(entry);
  }

  /**
   * Core logging method
   */
  private log(level: LogLevel, message: string, metadata?: LogMetadata): void {
    const entry: LogEntry = {
      level,
      message,
      timestamp: new Date().toISOString(),
      metadata,
    };

    // In production, only log WARN and ERROR levels
    // Suppress INFO and DEBUG to reduce console noise
    if (!this.isDevelopment() && (level === LogLevel.INFO || level === LogLevel.DEBUG)) {
      return;
    }

    switch (level) {
      case LogLevel.DEBUG:
        break;
      case LogLevel.INFO:
        break;
      case LogLevel.WARN:
        break;
      case LogLevel.ERROR:
        break;
    }
    if (!this.isDevelopment() && level === LogLevel.ERROR) {
      this.sendToExternalService(entry);
    }
  }

  private sendToExternalService(_entry: LogEntry): void { void 0 }

  /**
   * Debug level logging (development only)
   */
  debug(message: string, metadata?: LogMetadata): void {
    this.log(LogLevel.DEBUG, message, metadata);
  }

  /**
   * Info level logging
   */
  info(message: string, metadata?: LogMetadata): void {
    this.log(LogLevel.INFO, message, metadata);
  }

  /**
   * Warning level logging
   */
  warn(message: string, metadata?: LogMetadata): void {
    this.log(LogLevel.WARN, message, metadata);
  }

  /**
   * Error level logging
   */
  error(message: string, metadata?: LogMetadata): void {
    this.log(LogLevel.ERROR, message, metadata);
  }
}

// Export singleton instance
export const logger = new Logger();
